import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Trophy, Clock, CheckCircle2, History, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { animalNames, animalEmojis, ResultRow } from "./constants";
import { buildSorteios, AdvancedFilters } from "./analiseEngine";
import { gerarHistoricoPremiacoes, HistoricoPremiacoes, rotuloDiasSemana, rotuloModalidades, rotuloPeriodoHistorico, rotuloPremios, rotuloValidade, TURNO_ORDEM } from "./palpitesUtils";
import { HistoricoPremiacoesDetalhes } from "./PalpitesAvancadosCards";
import DezenasFrenteFundoCard from "./DezenasFrenteFundoCard";
import { AnaliseFrenteFundo } from "./frenteFundoEngine";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";

interface PalpiteRow {
  id: string;
  source: string;
  palpites: any;
  data_referencia: string;
  conferido: boolean;
  premiado: boolean;
  detalhes_premio: any;
  created_at: string;
  filtros_usados: any;
}

type Draw = { id: string; data: string; turno: string; rows: any[] };

const combinations = (values: string[], size: number) => {
  const result: string[] = [];
  const walk = (start: number, current: string[]) => {
    if (current.length === size) { result.push([...current].sort((a, b) => Number(a) - Number(b)).join("-")); return; }
    for (let i = start; i < values.length; i++) walk(i + 1, [...current, values[i]]);
  };
  walk(0, []);
  return new Set(result);
};

const groupDraws = (rows: any[], source: string): Draw[] => {
  const map = new Map<string, Draw>();
  rows.forEach((row) => {
    const turno = source === "rio" ? row.turno : "FED";
    const id = `${row.data}|${turno}`;
    const draw = map.get(id) || { id, data: row.data, turno, rows: [] };
    draw.rows.push(row);
    map.set(id, draw);
  });
  return [...map.values()]
    .filter((draw) => new Set(draw.rows.map((row) => row.posicao)).size >= 5)
    .sort((a, b) => a.data.localeCompare(b.data) || TURNO_ORDEM.indexOf(a.turno) - TURNO_ORDEM.indexOf(b.turno));
};

const normalizeCombo = (value: string) => (value.match(/\d+/g) || []).map(Number).map(String).sort((a, b) => Number(a) - Number(b)).join("-");

const MODELOS: Record<string, string> = {
  avancada: "Análise Avançada automática",
  estatisticas: "Gerador pelas Estatísticas",
  ia: "Palpite Guru IA",
};

const modalidadePelosPalpites = (palpites: any) => {
  const modalidades = [
    ["milhares", "milhar"],
    ["centenas", "centena"],
    ["dezenas", "dezena"],
    ["grupos", "grupo"],
    ["duques_dezenas", "duque_dezena"],
    ["duques_grupos", "duque_grupo"],
    ["ternos_dezenas", "terno_dezena"],
    ["ternos_grupos", "terno_grupo"],
  ];
  return modalidades.filter(([key]) => Array.isArray(palpites?.[key]) && palpites[key].length > 0).map(([, value]) => value);
};

const filtroFoiRegistrado = (filtros: any, chave: string) =>
  Boolean(filtros && Object.prototype.hasOwnProperty.call(filtros, chave));

const MODALIDADES_HISTORICO: Array<{
  chave: string;
  titulo: string;
  prefixo?: string;
  grupos?: boolean;
}> = [
  { chave: "milhares", titulo: "Milhares", prefixo: "" },
  { chave: "centenas", titulo: "Centenas", prefixo: "C" },
  { chave: "dezenas", titulo: "Dezenas", prefixo: "D" },
  { chave: "grupos", titulo: "Grupos", grupos: true },
  { chave: "duques_dezenas", titulo: "Duques de dezenas", prefixo: "" },
  { chave: "duques_grupos", titulo: "Duques de grupos", prefixo: "" },
  { chave: "ternos_dezenas", titulo: "Ternos de dezenas", prefixo: "" },
  { chave: "ternos_grupos", titulo: "Ternos de grupos", prefixo: "" },
];

const PalpitesPorModalidade: React.FC<{ palpites: any }> = ({ palpites }) => {
  const modalidades = MODALIDADES_HISTORICO.filter(
    ({ chave }) => Array.isArray(palpites?.[chave]) && palpites[chave].length > 0
  );

  return (
    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
      {modalidades.map(({ chave, titulo, prefixo, grupos }) => (
        <div key={chave} className="rounded-md border border-border bg-muted/30 p-2.5">
          <p className="mb-1.5 text-[11px] font-bold text-foreground">{titulo}</p>
          <div className="flex flex-wrap gap-1.5">
            {palpites[chave].map((valor: string | number, indice: number) => {
              if (grupos) {
                const grupo = Number(valor);
                return (
                  <span key={`${chave}-${indice}`} className="rounded bg-muted px-2 py-1 text-xs">
                    {animalEmojis[grupo] || ""} G{String(grupo).padStart(2, "0")}
                  </span>
                );
              }
              return (
                <span key={`${chave}-${indice}`} className="rounded bg-muted px-2 py-1 font-mono text-xs">
                  {prefixo}{String(valor).split("-").join(" + ")}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const PalpitesHistorico: React.FC = () => {
  const [palpites, setPalpites] = useState<PalpiteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [historicosAntigos, setHistoricosAntigos] = useState<Record<string, HistoricoPremiacoes>>({});

  useEffect(() => {
    fetchPalpites();
    const handler = () => fetchPalpites();
    window.addEventListener('palpites-ia:refresh', handler);
    return () => window.removeEventListener('palpites-ia:refresh', handler);
  }, []);

  useEffect(() => {
    const antigos = palpites.filter((palpite) =>
      palpite.filtros_usados?.origem === "avancada"
      && !palpite.filtros_usados?.historico_premiacoes
      && !historicosAntigos[palpite.id]
      && filtroFoiRegistrado(palpite.filtros_usados, "posicoes")
      && filtroFoiRegistrado(palpite.filtros_usados, "diasSemana")
      && filtroFoiRegistrado(palpite.filtros_usados, "turnos")
    );
    if (!antigos.length) return;

    const reconstruir = async () => {
      const datasInicio = antigos.map((palpite) => {
        const data = new Date(palpite.created_at);
        data.setUTCDate(data.getUTCDate() - 90);
        return data.toISOString().slice(0, 10);
      });
      const inicio = [...datasInicio].sort()[0];
      if (!inicio) return;
      const [rio, federal] = await Promise.all([
        supabase.from("deu_no_poste_resultados").select("data, turno, posicao, milhar, grupo").gte("data", inicio),
        supabase.from("deu_no_poste_federal").select("data, posicao, milhar, grupo").gte("data", inicio),
      ]);
      if (rio.error || federal.error) return;
      const linhas: Array<ResultRow & { fonte: string }> = [
        ...(rio.data || []).map((row) => ({ ...row, fonte: "rio" })),
        ...(federal.data || []).map((row) => ({ ...row, turno: "FED", fonte: "federal" })),
      ];
      const calculados: Record<string, HistoricoPremiacoes> = {};
      antigos.forEach((palpite) => {
        const filtrosSalvos = palpite.filtros_usados || {};
        const filtros: AdvancedFilters = {
          fontes: [palpite.source],
          posicoes: Array.isArray(filtrosSalvos.posicoes) ? filtrosSalvos.posicoes.map(Number) : [],
          diasSemana: Array.isArray(filtrosSalvos.diasSemana) ? filtrosSalvos.diasSemana.map(Number) : [],
          turnos: Array.isArray(filtrosSalvos.turnos) ? filtrosSalvos.turnos.map(String) : [],
          modalidades: [],
        };
        const dataFinal = palpite.created_at.slice(0, 10);
        const sorteios = buildSorteios(linhas.filter((linha) => linha.data <= dataFinal), filtros);
        calculados[palpite.id] = gerarHistoricoPremiacoes(palpite.palpites, sorteios, dataFinal);
      });
      setHistoricosAntigos((atuais) => ({ ...atuais, ...calculados }));
    };
    reconstruir();
  }, [historicosAntigos, palpites]);

  const fetchPalpites = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    const { data, error } = await supabase
      .from('palpites_ia_gerados' as any)
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (!error && data) {
      setPalpites(data as any);
    }
    setLoading(false);
  };

  const conferirPalpites = async () => {
    setChecking(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error("Sua sessão expirou. Entre novamente para conferir seus jogos.");

      let falhaAtualizacaoResultados: string | null = null;
      try {
        const { data: refreshData, error: refreshError } = await supabase.functions.invoke("fetch-deu-no-poste", {
          body: null,
        });
        if (refreshError) throw refreshError;
        if (!refreshData?.success) {
          throw new Error(refreshData?.message || "A fonte de resultados não respondeu corretamente.");
        }
      } catch (refreshError) {
        console.error("Erro ao atualizar resultados antes da conferência:", refreshError);
        falhaAtualizacaoResultados = refreshError instanceof Error
          ? refreshError.message
          : "Não foi possível atualizar a fonte de resultados.";
      }

      const pendentes = palpites.filter(p => !p.conferido);
      if (pendentes.length === 0) {
        toast({ title: "Tudo conferido", description: "Não há jogos pendentes nesta conta." });
        return;
      }

      let novasCorridas = 0;
      let novosAcertos = 0;
      let jogosAtualizados = 0;
      const falhas: string[] = [];

      for (const p of pendentes) {
        try {
          const marker = p.filtros_usados?.marcador_geracao || { data: p.data_referencia, turno: p.source === "rio" ? "COR" : "FED" };
          const target = p.source === "rio" ? 12 : 1;
          let rows: any[] = [];
          if (p.source === 'rio') {
            const { data, error } = await supabase
              .from('deu_no_poste_resultados')
              .select('milhar, grupo, data, turno, posicao')
              .gte('data', marker.data)
              .order('data', { ascending: true })
              .limit(300);
            if (error) throw error;
            rows = (data || []).filter((r) => r.data > marker.data || (r.data === marker.data && TURNO_ORDEM.indexOf(r.turno) > TURNO_ORDEM.indexOf(marker.turno)));
          } else {
            const { data, error } = await supabase
              .from('deu_no_poste_federal')
              .select('milhar, grupo, data, posicao')
              .gt('data', marker.data)
              .order('data', { ascending: true })
              .limit(100);
            if (error) throw error;
            rows = data || [];
          }

          const draws = groupDraws(rows, p.source).slice(0, target);
          const previous = Array.isArray(p.detalhes_premio?.acertos) ? p.detalhes_premio.acertos : [];
          const progressoAnterior = Number(p.detalhes_premio?.sorteios_conferidos) || 0;
          const acertos: any[] = [...previous];
          const hitKeys = new Set(previous.map((a: any) => `${a.tipo}|${a.valor}|${a.data}|${a.turno}|${a.posicoes || a.posicao}`));
          const fmtData = (d: string) => {
            const [, m, day] = d.split('-');
            return `${day}/${m}`;
          };

          const addHit = (hit: any) => {
            const key = `${hit.tipo}|${hit.valor}|${hit.data}|${hit.turno}|${hit.posicoes || hit.posicao}`;
            if (!hitKeys.has(key)) { hitKeys.add(key); acertos.push(hit); }
          };

          for (const draw of draws) {
            const base = { data: draw.data, dataFmt: fmtData(draw.data), turno: draw.turno };
            draw.rows.forEach((r) => {
              const corrida = { ...base, posicao: r.posicao };
              const dezena = r.milhar.slice(-2);
              const centena = r.milhar.slice(-3);
              p.palpites?.milhares?.forEach((m: string) => { if (m === r.milhar) addHit({ tipo: "Milhar", valor: m, ...corrida }); });
              p.palpites?.centenas?.forEach((c: string) => { if (c === centena) addHit({ tipo: "Centena", valor: c, ...corrida }); });
              p.palpites?.dezenas?.forEach((d: string) => { if (d === dezena) addHit({ tipo: "Dezena", valor: d, ...corrida }); });
              p.palpites?.grupos?.forEach((g: number) => { if (r.posicao === 1 && Number(g) === Number(r.grupo)) addHit({ tipo: "Grupo", valor: `${String(g).padStart(2, "0")} ${animalNames[g] || ""}`, ...corrida }); });
            });
            const dezenas = [...new Set<string>(draw.rows.map((r) => String(r.milhar).slice(-2)))];
            const grupos = [...new Set<string>(draw.rows.map((r) => String(r.grupo)))];
            const modalidades = [
              { key: "duques_dezenas", tipo: "Duque de dezena", values: dezenas, size: 2 },
              { key: "ternos_dezenas", tipo: "Terno de dezena", values: dezenas, size: 3 },
              { key: "duques_grupos", tipo: "Duque de grupo", values: grupos, size: 2 },
              { key: "ternos_grupos", tipo: "Terno de grupo", values: grupos, size: 3 },
            ];
            modalidades.forEach((mod) => {
              const drawn = combinations(mod.values, mod.size);
              (p.palpites?.[mod.key] || []).forEach((value: string) => {
                const normalized = normalizeCombo(value);
                if (drawn.has(normalized)) {
                  const wanted = new Set(normalized.split("-"));
                  const positions = draw.rows.filter((r) => wanted.has(mod.key.includes("grupo") ? String(r.grupo) : r.milhar.slice(-2))).map((r) => r.posicao);
                  addHit({ tipo: mod.tipo, valor: normalized.split("-").join(" + "), ...base, posicoes: [...new Set(positions)].sort((a, b) => a - b).join(", ") });
                }
              });
            });
          }

          const isPremiado = acertos.length > 0;
          const completed = draws.length >= target;

          const { error: updateError } = await supabase.from('palpites_ia_gerados' as any).update({
            conferido: completed,
            premiado: isPremiado,
            detalhes_premio: { acertos, sorteios_conferidos: draws.length, sorteios_alvo: target, ultima_verificacao: new Date().toISOString() },
          } as any).eq('id', p.id).eq('user_id', user.id);
          if (updateError) throw updateError;

          novasCorridas += Math.max(0, draws.length - progressoAnterior);
          novosAcertos += Math.max(0, acertos.length - previous.length);
          jogosAtualizados += 1;
        } catch (error) {
          console.error(`Erro ao conferir o jogo ${p.id}:`, error);
          falhas.push(error instanceof Error ? error.message : "Falha ao conferir um jogo");
        }
      }

      await fetchPalpites();
      if (falhas.length > 0) {
        toast({
          title: jogosAtualizados > 0 ? "Conferência parcialmente concluída" : "Não foi possível conferir",
          description: jogosAtualizados > 0 ? `${jogosAtualizados} jogo(s) atualizado(s). ${falhas.length} apresentou(aram) erro.` : falhas[0],
          variant: "destructive",
        });
      } else if (novasCorridas === 0) {
        toast({
          title: falhaAtualizacaoResultados ? "Não foi possível atualizar os resultados" : "Aguardando próximos resultados",
          description: falhaAtualizacaoResultados
            ? `A conferência manteve o progresso salvo, mas a atualização da fonte falhou: ${falhaAtualizacaoResultados}`
            : "Ainda não há uma nova corrida completa após os jogos salvos.",
          variant: falhaAtualizacaoResultados ? "destructive" : "default",
        });
      } else {
        toast({
          title: "Conferência atualizada",
          description: `${novasCorridas} nova(s) corrida(s) conferida(s)${novosAcertos > 0 ? ` e ${novosAcertos} novo(s) prêmio(s) encontrado(s)` : ". Nenhum novo prêmio encontrado"}.${falhaAtualizacaoResultados ? " A fonte não pôde ser atualizada; foram usados os resultados já salvos." : ""}`,
        });
      }
    } catch (err) {
      console.error('Erro ao conferir:', err);
      toast({ title: "Não foi possível conferir", description: err instanceof Error ? err.message : "Tente novamente em instantes.", variant: "destructive" });
    } finally {
      setChecking(false);
    }
  };

  const apagarPalpite = async (id: string) => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      toast({ title: "Sua sessão expirou", description: "Entre novamente para apagar seus jogos.", variant: "destructive" });
      return;
    }
    const { error } = await supabase.from("palpites_ia_gerados" as any).delete().eq("id", id).eq("user_id", user.id);
    if (error) {
      toast({ title: "Não foi possível apagar", description: error.message, variant: "destructive" });
      return;
    }
    setPalpites((current) => current.filter((item) => item.id !== id));
    toast({ title: "Jogo apagado" });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (palpites.length === 0) return null;

  const pendentes = palpites.filter(p => !p.conferido).length;

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 gap-2">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <History className="h-4 w-4 sm:h-5 sm:w-5" />
          Histórico de Jogos e Palpites
        </CardTitle>
        {pendentes > 0 && (
          <Button size="sm" onClick={conferirPalpites} disabled={checking} className="gap-1 w-full sm:w-auto">
            {checking ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            {checking ? "Conferindo..." : `Conferir (${pendentes})`}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {palpites.map(p => (
          <div key={p.id} className="flex flex-col gap-1 p-3 rounded-lg border bg-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant={p.source === 'rio' ? 'default' : 'secondary'} className="text-xs">{p.source === 'rio' ? 'Paratodos Rio' : 'Federal'}</Badge>
                <Badge variant="outline" className="text-[10px]">{p.filtros_usados?.origem === "estatisticas" ? "Estatísticas" : p.filtros_usados?.origem === "avancada" ? "Análise Avançada" : "Palpite Guru IA"}</Badge>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {new Date(p.created_at).toLocaleString('pt-BR', {
                    timeZone: 'America/Sao_Paulo',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {p.premiado ? (
                  <Badge className="bg-green-600 text-white gap-1 text-xs">
                    <Trophy className="h-3 w-3" /> Premiado!
                  </Badge>
                ) : p.conferido ? (
                  <Badge variant="outline" className="text-xs gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Conferido
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs gap-1 text-muted-foreground">
                    <Clock className="h-3 w-3" /> Pendente
                  </Badge>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" aria-label="Apagar jogo"><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Apagar este jogo?</AlertDialogTitle><AlertDialogDescription>O jogo e suas informações de premiação serão removidos do seu histórico.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => apagarPalpite(p.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Apagar</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            <div className="mt-1 rounded-md bg-muted/60 px-2.5 py-2 text-[11px] text-muted-foreground">
              <p className="mb-1 font-bold text-foreground">Dados usados na geração</p>
              <p><span className="font-semibold text-foreground">Modelo:</span> {MODELOS[p.filtros_usados?.origem] || (p.filtros_usados?.source ? "Palpite Guru IA" : "Não registrado na geração")}</p>
              <p><span className="font-semibold text-foreground">Loteria:</span> {p.source === "rio" ? "Paratodos Rio" : "Federal"}</p>
              <p><span className="font-semibold text-foreground">Prêmios:</span> {filtroFoiRegistrado(p.filtros_usados, "posicoes") ? rotuloPremios(p.filtros_usados?.posicoes) : "Não registrado na geração"}</p>
              {p.source === "rio" && (
                <p><span className="font-semibold text-foreground">Turnos:</span> {filtroFoiRegistrado(p.filtros_usados, "turnos") ? (Array.isArray(p.filtros_usados?.turnos) && p.filtros_usados.turnos.length ? p.filtros_usados.turnos.join(", ") : "Todos os turnos") : "Não registrado na geração"}</p>
              )}
              {p.filtros_usados?.origem === "avancada" && (
                <>
                  <p><span className="font-semibold text-foreground">Dias:</span> {rotuloDiasSemana(p.filtros_usados?.diasSemana)}</p>
                  <p><span className="font-semibold text-foreground">Modalidades selecionadas:</span> {rotuloModalidades(p.filtros_usados?.modalidades)}</p>
                </>
              )}
              <p><span className="font-semibold text-foreground">Palpites gerados:</span> {rotuloModalidades(modalidadePelosPalpites(p.palpites))}</p>
              <p><span className="font-semibold text-foreground">Período histórico analisado:</span> {rotuloPeriodoHistorico(p.filtros_usados)}</p>
              <p>
                <span className="font-semibold text-foreground">Corridas analisadas:</span>{" "}
                {p.filtros_usados?.total_sorteios_analisados ?? "Quantidade não registrada na geração"}
              </p>
              <p>
                <span className="font-semibold text-foreground">Resultados analisados:</span>{" "}
                {p.filtros_usados?.total_resultados_analisados ?? p.filtros_usados?.total ?? "Quantidade não registrada na geração"}
              </p>
              <p><span className="font-semibold text-foreground">Validade:</span> {rotuloValidade(p.source, p.filtros_usados?.janela_sorteios)}</p>
            </div>

            {!p.conferido && (
              <div className="mt-1 flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-2.5 py-2 text-xs">
                <Clock className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span><strong>{p.detalhes_premio?.sorteios_conferidos || 0} de {p.detalhes_premio?.sorteios_alvo || (p.source === "rio" ? 12 : 1)}</strong> {p.source === "rio" ? "corridas futuras conferidas" : "extração futura conferida"}</span>
              </div>
            )}

            <PalpitesPorModalidade palpites={p.palpites} />

            {p.filtros_usados?.origem === "avancada" && p.filtros_usados?.dezenas_frente_fundo && (
              <div className="mt-2">
                <DezenasFrenteFundoCard
                  analise={p.filtros_usados.dezenas_frente_fundo as AnaliseFrenteFundo}
                  historico
                />
              </div>
            )}

            {p.filtros_usados?.origem === "avancada" && (p.filtros_usados?.historico_premiacoes || historicosAntigos[p.id]) && (
              <div className="mt-2 rounded-md border border-border bg-muted/20 p-2.5">
                <p className="flex items-center gap-1.5 text-[11px] font-bold text-foreground">
                  <History className="h-3.5 w-3.5" /> Histórico na geração e motivo da seleção
                </p>
                <HistoricoPremiacoesDetalhes historico={p.filtros_usados?.historico_premiacoes || historicosAntigos[p.id]} />
                <p className="mt-2 text-[10px] text-muted-foreground">Este histórico explica a seleção estatística e não garante premiação futura.</p>
              </div>
            )}
            {p.filtros_usados?.origem === "avancada" && !p.filtros_usados?.historico_premiacoes && !historicosAntigos[p.id] && (
              <p className="mt-2 rounded-md border border-border bg-muted/20 px-2.5 py-2 text-[11px] text-muted-foreground">
                Histórico de premiações não registrado na geração deste jogo.
              </p>
            )}

            {p.premiado && p.detalhes_premio?.acertos && (
              <div className="mt-2 text-xs space-y-1">
                {p.detalhes_premio.acertos.map((a: any, i: number) => (
                  <div key={i} className="text-green-700 font-medium">
                    ✅ {typeof a === 'string' ? a : `${a.tipo} ${a.valor} — ${a.dataFmt || ''} ${a.turno || ''} ${a.posicoes ? `prêmios ${a.posicoes}` : a.posicao ? a.posicao + 'º prêmio' : ''}`.trim()}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default PalpitesHistorico;
